/**
 * Phase 2A closeout — authoritative question display numbers.
 *
 * The visible "Q<n>" label for any question associated with a finding, CAP
 * item, roadmap item, report row or appendix entry MUST come from the frozen
 * per-assessment ordinal stored in `assessment_question_applicability`.
 *
 * It must never be derived from:
 *   - an array index
 *   - the position in a filtered list
 *   - the runtime position in `questionnaire_items`
 *   - a question id / key prefix
 *
 * Legacy rows with no frozen set fall back to the grandfathered questionnaire
 * order, and that fallback is explicitly flagged (never silent).
 */

export type OrdinalSource = "frozen" | "fallback_questionnaire_order";

export interface QuestionOrdinal {
  question_id: number;
  display_ordinal: number;
  ordinal_source: OrdinalSource;
}

export type OrdinalMap = Map<number, QuestionOrdinal>;

export function buildOrdinalMap(rows: Array<Partial<QuestionOrdinal>> | null | undefined): OrdinalMap {
  const m: OrdinalMap = new Map();
  for (const r of rows ?? []) {
    if (r?.question_id == null || r?.display_ordinal == null) continue;
    const source: OrdinalSource =
      r.ordinal_source === "frozen" ? "frozen" : "fallback_questionnaire_order";
    m.set(Number(r.question_id), {
      question_id: Number(r.question_id),
      display_ordinal: Number(r.display_ordinal),
      ordinal_source: source,
    });
  }
  return m;
}

/**
 * Resolve the display ordinal for a question.
 * Returns `null` when unknown — callers must render a neutral label rather
 * than inventing a number from list position.
 */
export function resolveDisplayOrdinal(
  map: OrdinalMap | undefined | null,
  questionId: number | null | undefined,
  context = "unknown",
): number | null {
  if (questionId == null) return null;
  const hit = map?.get(Number(questionId));
  if (!hit) {
    console.warn(
      `[questionOrdinals] no frozen ordinal for question_id=${questionId} (${context}); rendering neutral label`,
    );
    return null;
  }
  if (hit.ordinal_source !== "frozen") {
    console.warn(
      `[questionOrdinals] fallback ordinal used for question_id=${questionId} (${context}); source=${hit.ordinal_source}`,
    );
  }
  return hit.display_ordinal;
}

/** Canonical "Q7" style label. Neutral when no authoritative ordinal exists. */
export function questionLabel(ordinal: number | null | undefined, neutral = "Question"): string {
  return ordinal == null ? neutral : `Q${ordinal}`;
}

/**
 * Build a deterministic fallback ordinal map from the *rendered* question
 * order, used only while frozen ordinals are unavailable (loading or missing).
 *
 * This exists so that the number a respondent SEES on a card and the number
 * quoted in a validation message can never disagree: both resolve through the
 * same merged map.
 */
export function buildFallbackOrdinalMap(
  rows: Array<{ question_id: number }> | null | undefined,
): OrdinalMap {
  const m: OrdinalMap = new Map();
  let n = 0;
  for (const r of rows ?? []) {
    if (r?.question_id == null) continue;
    n += 1;
    m.set(Number(r.question_id), {
      question_id: Number(r.question_id),
      display_ordinal: n,
      ordinal_source: "fallback_questionnaire_order",
    });
  }
  return m;
}

/**
 * Merge maps with the first argument winning (frozen ordinals take precedence
 * over the positional fallback).
 */
export function mergeOrdinalMaps(
  preferred: OrdinalMap | undefined | null,
  fallback: OrdinalMap | undefined | null,
): OrdinalMap {
  const m: OrdinalMap = new Map(fallback ?? []);
  for (const [k, v] of preferred ?? []) m.set(k, v);
  return m;
}
